import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import businessOnboardingApi from '../../api/businessOnboarding';
import {
  validateBusinessInfo,
} from '../../validators/businessOnboarding';
import BusinessInfoStep from './steps/BusinessInfoStep';
import BusinessLocationStep from './steps/BusinessLocationStep';

/**
 * Business Onboarding Wizard
 * Multi-step wizard with draft support for business onboarding
 */
const BusinessOnboardingWizard = () => {
  const { businessId } = useParams();
  const navigate = useNavigate();

  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [step1ValidateTick, setStep1ValidateTick] = useState(0);
  const [step2ValidateTick, setStep2ValidateTick] = useState(0);
  const [currentStep, setCurrentStep] = useState(1);

  const formatSubmitError = (err) => {
    const apiCode = err?.response?.data?.code;
    const apiMessage = err?.response?.data?.message;

    if (apiCode === 'location_letter_provider_not_configured') {
      return 'Location verification letters are temporarily unavailable. Please contact support to enable the mail provider configuration.';
    }

    if (apiCode === 'verification_temporarily_unavailable') {
      return 'Address verification is temporarily unavailable right now. Please try again in a minute.';
    }

    if (apiCode === 'imprecise_address') {
      return 'We could not precisely verify this address. Please check street name and house number.';
    }

    if (apiCode === 'incomplete_address') {
      return 'Please provide a complete address including house number.';
    }

    return apiMessage || err?.message || 'Failed to start Stripe verification';
  };

  // Step data (single-step onboarding)
  const [stepData, setStepData] = useState({
    1: {},
    2: {},
  });

  // Load business
  useEffect(() => {
    if (businessId) {
      loadBusinessData();
    } else {
      setLoading(false);
    }
  }, [businessId]);

  const loadBusinessData = async () => {
    try {
      setLoading(true);
      const response = await businessOnboardingApi.getBusiness(businessId);
      if (response.success) {
        setBusiness(response.data.business);
        
        // Pre-fill step data from existing business
        setStepData({
          1: extractBusinessInfo(response.data.business),
          2: extractBusinessLocation(response.data.business),
        });
      }
    } catch (err) {
      console.error('Error loading business:', err);
      setError(err.response?.data?.message || 'Failed to load business data');
    } finally {
      setLoading(false);
    }
  };

  const extractBusinessInfo = (business) => ({
    businessName: business.business_name || '',
    legalBusinessName: business.legal_business_name || '',
    categoryGroup: business.category_group || '',
    categoryDetail: business.category_detail || '',
    bio: business.bio || '',
    contactPhone: business.contact_phone || '',
    websiteUrl: business.website_url || '',
    profilePictureUrl: business.profile_picture_url || '',
    coverImageUrl: business.cover_image_url || '',
    taxId: business.tax_id || '',
    acceptsReservations: business.accepts_reservations || false,
    sellsProducts: business.sells_products || false,
    hasPhysicalLocation: business.has_physical_location || false,
  });

  const extractBusinessLocation = (business) => ({
    streetAddress: business.street_address || '',
    apartmentOrUnit: business.apartment_or_unit || '',
    floorNumber: business.floor_number || '',
    isGroundFloor: business.is_ground_floor || false,
    city: business.city || '',
    stateProvince: business.state_province || '',
    zipCode: business.zip_code || '',
    countryCode: business.country_code || '',
    fullAddress: business.full_address || '',
  });

  const handleStepDataChange = (data) => {
    setStepData((prev) => ({
      ...prev,
      1: { ...prev[1], ...data },
    }));
  };

  const handleLocationDataChange = (data) => {
    setStepData((prev) => ({
      ...prev,
      2: { ...prev[2], ...data },
    }));
  };

  const validateLocationStep = () => {
    const location = stepData[2] || {};
    return !!String(location.streetAddress || '').trim()
      && !!String(location.city || '').trim()
      && !!String(location.zipCode || '').trim()
      && !!String(location.fullAddress || '').trim()
      && /^[A-Za-z]{2}$/.test(String(location.countryCode || '').trim());
  };

  const saveStepOneData = async () => {
    const data = stepData[1] || {};

    if (!businessId) {
      const response = await businessOnboardingApi.createBusiness(data);
      const newBusinessId = response?.data?.business?.id;
      setBusiness(response?.data?.business || null);
      if (!newBusinessId) {
        throw new Error('Failed to create business');
      }
      navigate(`/business/onboarding/${newBusinessId}`, { replace: true });
      return newBusinessId;
    }

    const response = await businessOnboardingApi.updateBusiness(businessId, data);
    if (response?.data?.business) {
      setBusiness(response.data.business);
    }
    return businessId;
  };

  const handleSubmit = async () => {
    try {
      setSaving(true);
      setError(null);

      const validation = validateBusinessInfo(stepData[1] || {});
      if (!validation.isValid) {
        setStep1ValidateTick((tick) => tick + 1);
        setError('Please fix the business information fields before continuing.');
        setSaving(false);
        return;
      }

      const resolvedBusinessId = await saveStepOneData();
      const hasPhysicalLocation = !!stepData?.[1]?.hasPhysicalLocation;

      if (hasPhysicalLocation) {
        if (!validateLocationStep()) {
          setStep2ValidateTick((tick) => tick + 1);
          setCurrentStep(2);
          setError('Please complete the location details before continuing.');
          setSaving(false);
          return;
        }
        await businessOnboardingApi.upsertLocation(resolvedBusinessId, stepData[2]);
      }

      await businessOnboardingApi.completeOnboarding(resolvedBusinessId);

      const wantsPayments = !!stepData?.[1]?.acceptsReservations || !!stepData?.[1]?.sellsProducts;
      if (hasPhysicalLocation) {
        const checkout = await businessOnboardingApi.createLocationLetterCheckout(resolvedBusinessId);
        const checkoutUrl = checkout?.data?.checkoutUrl;
        if (!checkoutUrl) {
          throw new Error('Missing location verification checkout URL');
        }
        window.location.assign(checkoutUrl);
        return;
      }

      if (wantsPayments) {
        const link = await businessOnboardingApi.getStripeOnboardingLink(resolvedBusinessId);
        const onboardingUrl = link?.data?.onboardingUrl;
        if (!onboardingUrl) {
          throw new Error('Missing Stripe onboarding URL');
        }
        window.location.assign(onboardingUrl);
        return;
      }

      navigate(`/business-admin?business_id=${resolvedBusinessId}`);
    } catch (err) {
      console.error('Error submitting:', err);
      setError(formatSubmitError(err));
      setSaving(false);
    }
  };

  const hasPhysicalLocation = !!stepData?.[1]?.hasPhysicalLocation;
  const totalSteps = hasPhysicalLocation ? 2 : 1;

  useEffect(() => {
    if (!hasPhysicalLocation && currentStep !== 1) {
      setCurrentStep(1);
    }
  }, [hasPhysicalLocation, currentStep]);

  const handleNext = () => {
    const validation = validateBusinessInfo(stepData[1] || {});
    if (!validation.isValid) {
      setStep1ValidateTick((tick) => tick + 1);
      setError('Please fix the business information fields before continuing.');
      return;
    }
    setError(null);
    if (hasPhysicalLocation) {
      setCurrentStep(2);
      return;
    }
    handleSubmit();
  };

  const handlePrimaryAction = () => {
    if (currentStep === 1) {
      handleNext();
      return;
    }
    handleSubmit();
  };

  const handleSaveDraft = async () => {
    try {
      setSaving(true);
      setError(null);

      await saveStepOneData();

      alert('Draft saved successfully!');
    } catch (err) {
      console.error('Error saving draft:', err);
      setError(err.response?.data?.message || 'Failed to save draft');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="tailwind">
        <div className="flex min-h-[100dvh] w-full flex-col items-center justify-center gap-4 bg-gray-50 px-4">
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-gray-200 border-t-indigo-600" />
          <p className="text-sm text-gray-600">Loading onboarding wizard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="tailwind">
      <div className="h-[100dvh] w-full overflow-hidden bg-gray-50 px-4 py-6 md:px-6">
        <div className="mx-auto flex h-full w-full max-w-3xl flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        {/* Header area (non-scrolling) */}
        <div className="border-b border-gray-200 p-4 md:p-6">
          {/* Progress indicator */}
          <div className="mb-4">
            <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
              <div
                className="h-full bg-indigo-600 transition-all"
                style={{ width: `${(currentStep / totalSteps) * 100}%` }}
              />
            </div>
            <div className="mt-2 text-center text-sm text-gray-600">
              Step {currentStep} of {totalSteps}
            </div>
          </div>

          {/* Step indicators */}
          <div className="flex items-start justify-between gap-2">
            <div className="group flex min-w-0 flex-1 flex-col items-center gap-2">
              <div className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold ${currentStep === 1 ? 'bg-indigo-600 text-white' : 'bg-indigo-100 text-indigo-700'}`}>
                1
              </div>
              <div className="line-clamp-2 text-center text-[11px] font-semibold leading-snug text-indigo-700 md:text-xs">
                Business Information
              </div>
            </div>
            {hasPhysicalLocation && (
              <div className="group flex min-w-0 flex-1 flex-col items-center gap-2">
                <div className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold ${currentStep === 2 ? 'bg-indigo-600 text-white' : 'bg-indigo-100 text-indigo-700'}`}>
                  2
                </div>
                <div className="line-clamp-2 text-center text-[11px] font-semibold leading-snug text-indigo-700 md:text-xs">
                  Location
                </div>
              </div>
            )}
          </div>

          {/* Error message */}
          {error && (
            <div className="mt-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
              <span aria-hidden="true">⚠️</span>
              <span className="flex-1">{error}</span>
            </div>
          )}
        </div>

        {/* Scrollable step content */}
        <div className="min-h-0 flex-1 overflow-y-auto p-4 md:p-6 touch-pan-y overscroll-contain [-webkit-overflow-scrolling:touch]">
          {currentStep === 1 ? (
            <>
              <h2 className="mb-4 text-xl font-bold text-gray-900 md:text-2xl">Business Information</h2>
              <BusinessInfoStep
                data={stepData[1]}
                onChange={handleStepDataChange}
                validateTrigger={step1ValidateTick}
              />
            </>
          ) : (
            <>
              <h2 className="mb-4 text-xl font-bold text-gray-900 md:text-2xl">Business Location</h2>
              <BusinessLocationStep
                data={stepData[2]}
                onChange={handleLocationDataChange}
                validateTrigger={step2ValidateTick}
              />
            </>
          )}
        </div>

        {/* Actions (non-scrolling) */}
        <div className="flex flex-col gap-3 border-t border-gray-200 bg-white p-4 md:flex-row md:items-center md:justify-between md:gap-4 md:p-6">
          <div className="flex w-full gap-3 md:w-auto">
            {currentStep === 2 && (
              <button
                type="button"
                className="inline-flex w-full items-center justify-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 md:w-auto"
                onClick={() => setCurrentStep(1)}
                disabled={saving}
              >
                Back
              </button>
            )}
          </div>

          <div className="flex w-full flex-col gap-3 md:w-auto md:flex-row">
            <button
              type="button"
              className="inline-flex w-full items-center justify-center rounded-lg border border-indigo-600 bg-white px-4 py-2 text-sm font-semibold text-indigo-700 transition hover:bg-indigo-50 disabled:cursor-not-allowed disabled:opacity-50 md:w-auto"
              onClick={handleSaveDraft}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Draft'}
            </button>

            <button
              type="button"
              className="inline-flex w-full items-center justify-center rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50 md:w-auto"
              onClick={handlePrimaryAction}
              disabled={saving}
            >
              {saving
                ? 'Submitting...'
                : currentStep === 1
                  ? (hasPhysicalLocation ? 'Continue' : 'Continue to Verification')
                  : 'Continue to Verification'}
            </button>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessOnboardingWizard;
